// src/elements/hudReel/reelArrowBridge.ts
export interface ReelArrowOptions {
  delayMs?: number;        // default 3000
  hideOnReplay?: boolean;  // default true
}

export function attachReelArrowBridge(
  video: HTMLVideoElement,
  section: HTMLElement,
  opts: ReelArrowOptions = {}
): { detach(): void } {
  const ac = new AbortController();
  const sig = ac.signal;

  const delay = opts.delayMs ?? 1000;
  const hideOnReplay = opts.hideOnReplay ?? true;

  let revealTO: number | null = null;
  let shown = false;
  let countdownActive = false;
  let countdownTimers: number[] = [];
  let labelSwapTO: number | null = null;

  const getArrowButton = () =>
    section.querySelector<HTMLButtonElement>('button[data-scroll-arrow="down"]');

  const pulseArrow = () => {
    const btn = getArrowButton();
    if (!btn) return;
    btn.classList.remove("count-pulse");
    void btn.offsetWidth;
    btn.classList.add("count-pulse");
    window.setTimeout(() => btn.classList.remove("count-pulse"), 360);
  };

  const updateArrowLabel = (label: string, immediate = false) => {
    const btn = getArrowButton();
    const icon = btn?.querySelector("span");
    if (!icon) return;
    if (labelSwapTO) window.clearTimeout(labelSwapTO);
    if (immediate) {
      icon.textContent = label;
      icon.style.opacity = "";
      return;
    }
    icon.style.transition = "opacity 0.35s ease";
    icon.style.opacity = "0";
    labelSwapTO = window.setTimeout(() => {
      icon.textContent = label;
      icon.style.opacity = "1";
      labelSwapTO = null;
    }, 200);
  };

  const restoreArrowLabel = (immediate = false) => updateArrowLabel("↓", immediate);

  const clearCountdown = () => {
    countdownActive = false;
    countdownTimers.forEach((id) => window.clearTimeout(id));
    countdownTimers = [];
    if (labelSwapTO) {
      window.clearTimeout(labelSwapTO);
      labelSwapTO = null;
    }
    restoreArrowLabel(true);
  };

  const hasNextSection = () => {
    const idx = Number(section.dataset.sectionIndex);
    if (Number.isFinite(idx)) {
      if (document.querySelector(`section[data-section-index="${idx + 1}"]`)) return true;
    }
    let next = section.nextElementSibling;
    while (next && !(next instanceof HTMLElement)) next = next.nextElementSibling;
    return !!next;
  };

  const autoScrollNext = () => {
    const btn = getArrowButton();
    if (btn) {
      btn.click();
      return true;
    }
    return false;
  };

  const beginCountdown = () => {
    if (!hasNextSection()) return;
    if (!getArrowButton()) return;
    clearCountdown();
    countdownActive = true;
    let current = 5;

    const INITIAL_ARROW_MS = 1000;
    const COUNT_SHOW_MS = 700;
    const COUNT_GAP_MS = 800;

    const schedule = (fn: () => void, ms: number) => {
      const id = window.setTimeout(fn, ms);
      countdownTimers.push(id);
    };

    const tick = () => {
      if (!countdownActive) return;
      if (current <= 0) {
        clearCountdown();
        autoScrollNext();
        return;
      }
      updateArrowLabel(String(current));
      pulseArrow();
      schedule(() => {
        restoreArrowLabel();
        schedule(() => {
          current -= 1;
          tick();
        }, COUNT_GAP_MS);
      }, COUNT_SHOW_MS);
    };

    schedule(tick, INITIAL_ARROW_MS);
  };

  const showArrow = () => {
    if (!hasNextSection()) return;
    if (shown) return;
    shown = true;

    // Let arrow.ts handle it (preferred).
    document.dispatchEvent(
      new CustomEvent("sc:arrow:show", {
        detail: { section, video, source: "reel", reason: "ended-delay" },
      })
    );

    // DOM fallback (if arrow.ts doesn’t listen):
    try {
      const el = document.querySelector<HTMLElement>(".scroll-arrow");
      if (el) {
        el.style.visibility = "visible";
        el.style.opacity = "1";
        el.style.pointerEvents = "auto";
        el.style.left = "50%";
        el.style.transform = "translateX(-50%)";
      }
    } catch {}

    beginCountdown();
  };

  const hideArrow = (reason = "replay-or-interrupt") => {
    shown = false;
    if (revealTO) { window.clearTimeout(revealTO); revealTO = null; }
    clearCountdown();

    document.dispatchEvent(
      new CustomEvent("sc:arrow:hide", {
        detail: { section, video, source: "reel", reason },
      })
    );

    // DOM fallback reset — let arrow.ts regain control
    try {
      const el = document.querySelector<HTMLElement>(".scroll-arrow");
      if (el) {
        el.style.opacity = "";
        el.style.pointerEvents = "";
        el.style.visibility = "";
      }
    } catch {}
  };

  const onEnded = () => {
    if (revealTO) window.clearTimeout(revealTO);
    revealTO = window.setTimeout(showArrow, delay);
  };

  const onPlay = () => { if (hideOnReplay) hideArrow("play"); };

  video.addEventListener("ended", onEnded, { signal: sig });
  video.addEventListener("play", onPlay, { signal: sig });
  video.addEventListener("seeking", () => hideArrow("seeking"), { signal: sig });
  video.addEventListener("emptied", () => hideArrow("emptied"), { signal: sig });

  section.addEventListener("pointerdown", () => clearCountdown(), { signal: sig });

  sig.addEventListener("abort", () => {
    if (revealTO) window.clearTimeout(revealTO);
    clearCountdown();
  });

  return { detach() { try { ac.abort(); } catch {} } };
}
